/**
 * RithmoStageView — Rithmo Stage screen-share view (1280×720)
 *
 * Redesigned from ScreenShareAgendaView to be cinematic and branded.
 * Optimized for Playwright frame capture at 5fps (SHARE_FPS default):
 *   - Ambient glow uses a 45s animation cycle — imperceptible between frames.
 *   - Wake-word rings use fixed-size opacity pulses (not expansion) so the
 *     beacon effect looks correct at any FPS without discrete size jumps.
 *   - All content changes appear as clean cuts (deliberate, film-like).
 *
 * Layout (720px tall):
 *   Header   56px  — Rithmo wordmark (left) + meeting title (right)
 *   Body     flex  — Pre-meeting agenda OR live item OR recap
 *   Capture  ~90px — Live decisions / action items as they're confirmed
 *   Footer   52px  — Active Rithmo banner (left) + elapsed clock (right)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useMeetingState,
  type MeetingAgendaItem,
} from '../context/MeetingStateContext';
import type { InterimMeetingAnalysisDTO, ScorecardDimension } from '../types/interimAnalysis';
import { decodeHtmlEntities } from '../utils/displayText';
import { useBanners, CANONICAL_BANNER_TYPES, type BannerPayload } from './BannerNotification';
import logoImage from '../../3 ICON.png';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CaptureEntry = {
  id: string;
  kind: 'decision' | 'action';
  text: string;
  appearedAtMs: number;
};

type TimeboxDisplay = {
  text: string;
  level: 'normal' | 'warning' | 'critical';
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtMinutes(sec: number | null): string {
  if (sec == null || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  return m > 0 ? `${m} min` : `${sec}s`;
}

function resolveTimebox(item: MeetingAgendaItem): TimeboxDisplay {
  const tb = item.timebox;
  if (!tb) {
    const dur = fmtMinutes(item.plannedDurationSec);
    return { text: dur, level: 'normal' };
  }
  if (tb.overtimeSec != null && tb.overtimeSec > 0) {
    const m = Math.floor(tb.overtimeSec / 60);
    const s = tb.overtimeSec % 60;
    return {
      text: m > 0 ? `${m}m ${s}s over` : `${s}s over`,
      level: 'critical',
    };
  }
  if (tb.remainingSec != null) {
    const r = tb.remainingSec;
    if (r > 300) return { text: `${Math.floor(r / 60)} min left`, level: 'normal' };
    if (r > 120) return { text: `${Math.floor(r / 60)} min left`, level: 'warning' };
    if (r > 0) return { text: `${r}s left`, level: 'critical' };
    return { text: 'Time up', level: 'critical' };
  }
  return { text: '', level: 'normal' };
}

function resolveEffectiveEndMs(
  scheduledEndMs: number | null | undefined,
  startedAtMs: number | null | undefined,
  items: MeetingAgendaItem[],
): number | null {
  if (scheduledEndMs) return scheduledEndMs;
  if (!startedAtMs) return null;
  const totalSec = items.reduce((s, i) => s + (i.plannedDurationSec ?? 0), 0);
  return totalSec > 0 ? startedAtMs + totalSec * 1000 : null;
}

function fmtElapsed(ms: number | null | undefined): string {
  if (!ms || !Number.isFinite(ms)) return '--:--';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

// ---------------------------------------------------------------------------
// Banner filter — only surface types that coincide with Rithmo's voice
// ---------------------------------------------------------------------------

const STAGE_BANNER_ALLOWLIST = new Set([
  CANONICAL_BANNER_TYPES.agenda_time_warning,
  CANONICAL_BANNER_TYPES.agenda_advance_recommendation,
  CANONICAL_BANNER_TYPES.agenda_item_complete,
  CANONICAL_BANNER_TYPES.topic_transition,
  CANONICAL_BANNER_TYPES.decision_required,
  CANONICAL_BANNER_TYPES.time_critical,
  CANONICAL_BANNER_TYPES.invite_to_speak,
  CANONICAL_BANNER_TYPES.info,
  CANONICAL_BANNER_TYPES.meeting_starting_soon,
  CANONICAL_BANNER_TYPES.meeting_started,
]);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RithmoGlow() {
  return <div className="rs-glow" aria-hidden />;
}

function Header({ title }: { title: string }) {
  return (
    <div className="rs-header">
      <div className="rs-brand">
        <img src={logoImage} className="rs-logo-img" alt="" aria-hidden />
        <span className="rs-wordmark">Rithmo</span>
      </div>
      {title && (
        <div className="rs-meeting-title">{decodeHtmlEntities(title)}</div>
      )}
    </div>
  );
}

function PreMeetingDisplay({ items }: { items: MeetingAgendaItem[] }) {
  if (items.length === 0) {
    return (
      <div className="rs-premeet">
        <div className="rs-premeet-label">Ready to begin</div>
      </div>
    );
  }
  return (
    <div className="rs-premeet">
      <div className="rs-premeet-label">Today's agenda</div>
      <ul className="rs-premeet-list">
        {items.map((item, idx) => {
          const dur = fmtMinutes(item.plannedDurationSec);
          return (
            <li key={item.agendaItemId} className="rs-premeet-row">
              <span className="rs-premeet-num">{idx + 1}</span>
              <span className="rs-premeet-title">
                {decodeHtmlEntities(item.title) || 'Agenda item'}
              </span>
              {dur && <span className="rs-premeet-dur">{dur}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function LiveItemDisplay({
  item,
  currentIdx,
  totalItems,
  speakerName,
}: {
  item: MeetingAgendaItem | null;
  currentIdx: number;
  totalItems: number;
  speakerName: string | null;
}) {
  if (!item) {
    return (
      <div className="rs-live">
        <div className="rs-live-title rs-live-title--empty">Meeting in progress</div>
      </div>
    );
  }

  const tb = resolveTimebox(item);

  return (
    <div className="rs-live">
      {currentIdx >= 0 && totalItems > 1 && (
        <div className="rs-live-progress">
          Item {currentIdx + 1} of {totalItems}
        </div>
      )}
      <div className="rs-live-title">
        {decodeHtmlEntities(item.title) || 'Current item'}
      </div>
      {(speakerName || tb.text) && (
        <div className="rs-live-meta">
          {speakerName && (
            <span className="rs-live-speaker">{speakerName}</span>
          )}
          {speakerName && tb.text && (
            <span className="rs-live-sep">·</span>
          )}
          {tb.text && (
            <span className={`rs-live-time rs-live-time--${tb.level}`}>
              {tb.text}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ScorecardBar({ dim }: { dim: ScorecardDimension }) {
  const isPending = dim.score == null;
  const pct = isPending ? 0 : Math.max(0, Math.min(100, dim.score!));
  return (
    <div className="rs-recap-bar-row">
      <div className="rs-recap-bar-meta">
        <span className="rs-recap-bar-label">{dim.label}</span>
        <span className="rs-recap-bar-score">
          {isPending ? '—' : Math.round(pct)}
        </span>
      </div>
      <div className="rs-recap-bar-track">
        <div
          className={`rs-recap-bar-fill${isPending ? ' rs-recap-bar-fill--pending' : ''}`}
          style={{ width: isPending ? '100%' : `${pct}%` }}
        />
      </div>
    </div>
  );
}

function RecapDisplay({
  items,
  captureLog,
  elapsedMs,
  interimAnalysis,
  liveScorecard,
}: {
  items: MeetingAgendaItem[];
  captureLog: CaptureEntry[];
  elapsedMs: number | null;
  interimAnalysis: InterimMeetingAnalysisDTO | null | undefined;
  liveScorecard: ScorecardDimension[] | null | undefined;
}) {
  const decisions = captureLog.filter((e) => e.kind === 'decision');
  const actions = captureLog.filter((e) => e.kind === 'action');

  const scorecard = interimAnalysis?.scorecard ?? liveScorecard ?? [];
  const overallScore = interimAnalysis?.overallScore ?? null;

  return (
    <div className="rs-recap-full">
      {/* Left: outputs */}
      <div className="rs-recap-outputs">
        <div className="rs-recap-header">
          <div className="rs-recap-full-heading">Meeting Complete</div>
          {elapsedMs && (
            <div className="rs-recap-full-duration">Duration · {fmtElapsed(elapsedMs)}</div>
          )}
        </div>

        {decisions.length > 0 && (
          <div className="rs-recap-outputs-group">
            <div className="rs-recap-outputs-group-label">
              <span>Decisions</span>
              <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 400, letterSpacing: 0 }}>
                {decisions.length}
              </span>
            </div>
            {decisions.slice(0, 5).map((e) => (
              <div key={e.id} className="rs-recap-output-item">
                <span className="rs-recap-output-dot" />
                <span className="rs-recap-output-text">{e.text}</span>
              </div>
            ))}
          </div>
        )}

        {actions.length > 0 && (
          <div className="rs-recap-outputs-group">
            <div className="rs-recap-outputs-group-label rs-recap-outputs-group-label--action">
              <span>Actions</span>
              <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 400, letterSpacing: 0 }}>
                {actions.length}
              </span>
            </div>
            {actions.slice(0, 5).map((e) => (
              <div key={e.id} className="rs-recap-output-item">
                <span className="rs-recap-output-dot rs-recap-output-dot--action" />
                <span className="rs-recap-output-text">{e.text}</span>
              </div>
            ))}
          </div>
        )}

        {decisions.length === 0 && actions.length === 0 && (
          <div className="rs-recap-empty">No captured outputs</div>
        )}
      </div>

      {/* Divider */}
      {scorecard.length > 0 && <div className="rs-recap-divider" />}

      {/* Right: scorecard */}
      {scorecard.length > 0 && (
        <div className="rs-recap-scorecard">
          <div className="rs-recap-outputs-heading">Scorecard</div>
          {overallScore != null && (
            <div className="rs-recap-score-overall">
              <span className="rs-recap-score-num">{Math.round(overallScore)}</span>
              <span className="rs-recap-score-label">Overall</span>
            </div>
          )}
          <div className="rs-recap-bars">
            {scorecard.map((dim) => (
              <ScorecardBar key={dim.key} dim={dim} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CaptureStrip({ entries }: { entries: CaptureEntry[] }) {
  const visible = entries.slice(-3).reverse();
  return (
    <div className="rs-capture">
      {visible.map((entry) => (
        <div
          key={entry.id}
          className={`rs-capture-entry rs-capture-entry--${entry.kind}`}
        >
          <span className="rs-capture-dot" />
          <span className="rs-capture-text">{entry.text}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Banner pill — replaces the old footer text; floats between body and capture
// ---------------------------------------------------------------------------

type BannerTheme = 'teal' | 'amber' | 'coral' | 'red';

const BANNER_CONFIG: Record<string, { theme: BannerTheme; label: string }> = {
  [CANONICAL_BANNER_TYPES.agenda_time_warning]:           { theme: 'amber', label: 'Time' },
  [CANONICAL_BANNER_TYPES.agenda_advance_recommendation]: { theme: 'amber', label: 'Agenda' },
  [CANONICAL_BANNER_TYPES.agenda_item_complete]:          { theme: 'teal',  label: 'Done' },
  [CANONICAL_BANNER_TYPES.decision_required]:             { theme: 'coral', label: 'Decision' },
  [CANONICAL_BANNER_TYPES.time_critical]:                 { theme: 'red',   label: 'Critical' },
  [CANONICAL_BANNER_TYPES.invite_to_speak]:               { theme: 'teal',  label: 'Speaking' },
  [CANONICAL_BANNER_TYPES.info]:                          { theme: 'teal',  label: 'Update' },
  [CANONICAL_BANNER_TYPES.meeting_starting_soon]:         { theme: 'teal',  label: 'Starting' },
  [CANONICAL_BANNER_TYPES.meeting_started]:               { theme: 'teal',  label: 'Live' },
  [CANONICAL_BANNER_TYPES.topic_transition]:              { theme: 'teal',  label: 'Next' },
};

function StageBannerPill({ banner }: { banner: BannerPayload }) {
  const cfg = BANNER_CONFIG[banner.type] ?? { theme: 'teal' as BannerTheme, label: 'Update' };
  const text = banner.message ?? banner.title ?? '';
  if (!text) return null;
  return (
    <div className={`rs-banner-pill rs-banner-pill--${cfg.theme}`}>
      <span className="rs-banner-pill-label">{cfg.label}</span>
      <span className="rs-banner-pill-text">{decodeHtmlEntities(text)}</span>
    </div>
  );
}

function BannerZone({ banner }: { banner: BannerPayload | null }) {
  return (
    <div className="rs-banner-zone">
      {banner && <StageBannerPill banner={banner} />}
    </div>
  );
}

function Footer({ elapsedMs }: { elapsedMs: number | null }) {
  return (
    <div className="rs-footer">
      <div className="rs-footer-right">
        <span className="rs-elapsed">{fmtElapsed(elapsedMs)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wake-word overlay — fires when "Hey Rithmo" is detected
// ---------------------------------------------------------------------------

function WakeWordOverlay() {
  return (
    <div className="rs-wake-overlay">
      <div className="rs-wake-glow--outer" aria-hidden />
      <div className="rs-wake-glow--inner" aria-hidden />
      <div className="rs-wake-logo-wrap" aria-hidden>
        <img src={logoImage} className="rs-wake-logo" alt="" />
        <div className="rs-wake-logo-wave" style={{ '--logo-mask': `url(${logoImage})` } as React.CSSProperties} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agenda time-warning glow — rose glow radiates outward from logo over 2 min
// ---------------------------------------------------------------------------

function TimeGlowOverlay({ progress }: { progress: number }) {
  // Glow expands outward from center: 10% radius → 110% (full screen + overflow)
  const outerStop  = (10 + 100 * progress).toFixed(0);
  const innerOpacity = (0.05 + 0.50 * progress).toFixed(3);
  // Logo shifts from teal (0°) to rose-pink (+166°) over 2 minutes
  const hueRotate  = (166 * progress).toFixed(0);
  const brightness = (1 + 0.15 * progress).toFixed(2);
  return (
    <div
      className="rs-time-glow"
      style={{
        background: `radial-gradient(circle at center, rgba(225,70,110,${innerOpacity}) 0%, transparent ${outerStop}%)`,
      }}
    >
      <img
        src={logoImage}
        aria-hidden
        className="rs-time-glow-logo"
        style={{ filter: `hue-rotate(${hueRotate}deg) brightness(${brightness})` }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Whole-meeting overrun overlay — escalating pulse over 30 minutes of overrun
// ---------------------------------------------------------------------------

export function MeetingOverrunOverlay({
  overrunMs,
  pulseIntensity,
}: {
  overrunMs: number;
  pulseIntensity: number;
}) {
  const overrunMin = overrunMs / 60_000;

  // Color: rose-pink → orange-red over 10–15 min overrun
  const orangeDrift = Math.min(1, Math.max(0, (overrunMin - 10) / 5));
  const r = Math.round(225 + 30 * orangeDrift);
  const g = Math.round(70  - 40 * orangeDrift);
  const b = Math.round(110 - 80 * orangeDrift);

  // Glow: steady floor that grows over 30 min + pulse contribution on top
  const glowBase  = 0.20 + 0.35 * Math.min(1, overrunMin / 30);
  const glowTotal = (glowBase + 0.25 * pulseIntensity).toFixed(3);

  // Logo scale: persists at 1× until 5 min, then grows to 2× by 10 min, plus wobble
  const growT      = Math.min(1, Math.max(0, (overrunMin - 5) / 5));
  const logoScale  = (1 + growT + 0.10 * pulseIntensity).toFixed(3);
  const brightness = (1.0 + 0.30 * pulseIntensity).toFixed(2);

  // Ghost echo: fades in after 15 min, slightly out-of-phase with pulse
  const ghostT       = Math.min(1, Math.max(0, (overrunMin - 15) / 3));
  const ghostOpacity = (ghostT * 0.35 * (1 - 0.5 * pulseIntensity)).toFixed(3);
  const ghostScale   = ((1 + growT) * 1.7).toFixed(3);

  // Background breathing: after 20 min
  const bgT       = Math.min(1, Math.max(0, (overrunMin - 20) / 5));
  const bgOpacity = (bgT * 0.18 * pulseIntensity).toFixed(3);

  return (
    <div className="rs-overrun-overlay">
      {bgT > 0 && (
        <div className="rs-overrun-bg" style={{ opacity: Number(bgOpacity) }} />
      )}
      <div
        className="rs-overrun-glow"
        style={{
          background: `radial-gradient(circle at center, rgba(${r},${g},${b},${glowTotal}) 0%, transparent 80%)`,
        }}
      />
      {ghostT > 0 && (
        <img
          src={logoImage}
          aria-hidden
          className="rs-overrun-logo rs-overrun-logo--ghost"
          style={{
            opacity: Number(ghostOpacity),
            transform: `translate(-50%, -50%) scale(${ghostScale})`,
            filter: `hue-rotate(166deg) brightness(0.6)`,
          }}
        />
      )}
      <img
        src={logoImage}
        aria-hidden
        className="rs-overrun-logo"
        style={{
          transform: `translate(-50%, -50%) scale(${logoScale})`,
          filter: `hue-rotate(166deg) brightness(${brightness})`,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Content mode — PDF page display with Rithmo watermark
// ---------------------------------------------------------------------------

function ContentModeDisplay({
  pageNumber,
  meetingId,
}: {
  pageNumber: number;
  meetingId: string;
}) {
  const src = `/api/meeting/${encodeURIComponent(meetingId)}/content-page?page=${pageNumber}`;
  return (
    <div className="rs-content-mode">
      <img src={src} alt="Shared content" className="rs-content-img" />
      <div className="rs-content-brand">
        <img src={logoImage} className="rs-content-logo" alt="" aria-hidden />
        <span className="rs-content-wordmark">Rithmo</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export default function RithmoStageView() {
  const { meetingState, meetingId } = useMeetingState();
  const { banners } = useBanners();

  const [captureLog, setCaptureLog] = useState<CaptureEntry[]>([]);
  const seenIds = useRef<Set<string>>(new Set());

  const lifecycle = meetingState?.lifecycle ?? null;

  // Local 1-second clock driven by startedAtMs so the counter advances smoothly
  // between WebSocket pushes (fixes stutter at 2fps Playwright capture).
  const startedAtMs = lifecycle?.startedAtMs ?? null;
  const serverElapsedMs = lifecycle?.meetingElapsedMs ?? null;
  const [localElapsedMs, setLocalElapsedMs] = useState<number | null>(serverElapsedMs);
  useEffect(() => {
    if (!startedAtMs) {
      setLocalElapsedMs(serverElapsedMs);
      return;
    }
    const tick = () => setLocalElapsedMs(Date.now() - startedAtMs);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAtMs, serverElapsedMs]);
  const status = lifecycle?.status ?? 'created';
  const agendaItems = meetingState?.agenda?.items ?? [];
  const activeItemId = meetingState?.agenda?.activeAgendaItemId ?? null;
  const currentItem =
    activeItemId
      ? (agendaItems.find((i) => i.agendaItemId === activeItemId) ?? null)
      : null;
  const currentIdx = currentItem
    ? agendaItems.findIndex((i) => i.agendaItemId === activeItemId)
    : -1;
  const outputsByItem = meetingState?.outputsByAgendaItem ?? {};
  const meetingTitle = meetingState?.meetingTitle ?? '';
  const interimAnalysis = meetingState?.interimAnalysis ?? null;
  const liveScorecard = meetingState?.liveScorecard ?? null;
  const participants = meetingState?.participants ?? {};
  const transcript = meetingState?.transcript;
  const docState = meetingState?.documentState ?? null;

  // Resolve current speaker name (skip the bot)
  const speakerId = transcript?.currentSpeaker?.participantId ?? null;
  const speakerParticipant = speakerId ? participants[speakerId] : null;
  const speakerName =
    speakerParticipant && !speakerParticipant.isBot
      ? (speakerParticipant.displayName ?? null)
      : null;

  // Track newly confirmed decisions and action items → live capture strip.
  // Uses a stable ref of seen IDs so re-renders don't re-add existing items.
  useEffect(() => {
    const newEntries: CaptureEntry[] = [];
    for (const outputs of Object.values(outputsByItem)) {
      for (const d of outputs.decisions) {
        const key = `d:${d.id}`;
        if (!seenIds.current.has(key)) {
          seenIds.current.add(key);
          newEntries.push({
            id: key,
            kind: 'decision',
            text: d.summary,
            appearedAtMs: Date.now(),
          });
        }
      }
      for (const a of outputs.actionItems) {
        const key = `a:${a.id}`;
        if (!seenIds.current.has(key)) {
          seenIds.current.add(key);
          newEntries.push({
            id: key,
            kind: 'action',
            text: a.owner ? `${a.summary} — ${a.owner}` : a.summary,
            appearedAtMs: Date.now(),
          });
        }
      }
    }
    if (newEntries.length > 0) {
      setCaptureLog((prev) => [...prev, ...newEntries].slice(-12));
    }
  }, [outputsByItem]);

  // Most recent allowed banner, only if it arrived within the last 45s.
  // Banners older than that are stale (e.g. "1 min left for item 1" persisting on item 2).
  const BANNER_TTL_MS = 45_000;
  const activeBanner =
    banners
      .filter((b) => STAGE_BANNER_ALLOWLIST.has(b.type) && Date.now() - b.receivedAt < BANNER_TTL_MS)
      .slice(-1)[0] ?? null;

  // Wake-word overlay: show while a thinking banner is in the context; hide immediately
  // when the backend dismisses it (removeBannersByType). In screenshare capture mode,
  // BannerFloatingLayer returns null so BannerNotification's 30s auto-dismiss never fires —
  // an 8s safety TTL here covers the case where the backend dismiss is slow or missing.
  const prevThinkingCountRef = useRef(0);
  const [isThinkingActive, setIsThinkingActive] = useState(false);
  const thinkingTtlRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const count = banners.filter((b) => b.type === CANONICAL_BANNER_TYPES.thinking).length;
    if (count > prevThinkingCountRef.current) {
      setIsThinkingActive(true);
      if (thinkingTtlRef.current) clearTimeout(thinkingTtlRef.current);
      thinkingTtlRef.current = setTimeout(() => setIsThinkingActive(false), 8_000);
    } else if (count === 0 && prevThinkingCountRef.current > 0) {
      if (thinkingTtlRef.current) clearTimeout(thinkingTtlRef.current);
      setIsThinkingActive(false);
    }
    prevThinkingCountRef.current = count;
  }, [banners]);

  // Time-warning glow: builds from a pale red vignette at the edges to a full-screen red wash
  // over 120s, starting when the first agenda_time_warning banner arrives for the current item.
  // Resets to 0 whenever the active agenda item changes.
  const [glowStartedAtMs, setGlowStartedAtMs] = useState<number | null>(null);
  const [glowProgress, setGlowProgress] = useState(0);
  const seenWarningBannerIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setGlowStartedAtMs(null);
    setGlowProgress(0);
    // Do NOT clear seenWarningBannerIdsRef here. Clearing it causes the old item's
    // warning banner (still in the banners array) to immediately re-trigger the glow
    // via the [banners] effect below, restarting it from the old receivedAt timestamp.
    // Old warning IDs stay "seen" permanently; new items emit new banner IDs.
  }, [activeItemId]);

  useEffect(() => {
    const latestWarning = banners
      .filter((b) => b.type === CANONICAL_BANNER_TYPES.agenda_time_warning)
      .slice(-1)[0] ?? null;
    if (!latestWarning || seenWarningBannerIdsRef.current.has(latestWarning.id)) return;
    seenWarningBannerIdsRef.current.add(latestWarning.id);
    setGlowStartedAtMs((prev) => prev ?? latestWarning.receivedAt);
  }, [banners]);

  useEffect(() => {
    if (glowStartedAtMs === null) return;
    const id = setInterval(() => {
      setGlowProgress(Math.min(1, (Date.now() - glowStartedAtMs) / 120_000));
    }, 500);
    return () => clearInterval(id);
  }, [glowStartedAtMs]);

  // Whole-meeting overrun animation — JS-driven pulse, FPS-independent
  const scheduledEndMs = lifecycle?.scheduledEndMs ?? null;
  const effectiveEndMs = useMemo(
    () => resolveEffectiveEndMs(scheduledEndMs, startedAtMs, agendaItems),
    [scheduledEndMs, startedAtMs, agendaItems],
  );
  const [overrunMs, setOverrunMs] = useState(0);
  const [pulseIntensity, setPulseIntensity] = useState(0);
  const pulsePhaseRef = useRef(0);

  useEffect(() => {
    if (!effectiveEndMs) return;
    const TICK_MS = 100;
    const id = setInterval(() => {
      const overrun = Math.max(0, Date.now() - effectiveEndMs);
      setOverrunMs(overrun);
      if (overrun > 0) {
        const overrunMin = overrun / 60_000;
        const period = Math.max(800, 5000 - 4200 * (overrunMin / 30));
        pulsePhaseRef.current = (pulsePhaseRef.current + TICK_MS / period) % 1;
        setPulseIntensity((1 - Math.cos(2 * Math.PI * pulsePhaseRef.current)) / 2);
      } else {
        pulsePhaseRef.current = 0;
        setPulseIntensity(0);
      }
    }, TICK_MS);
    return () => clearInterval(id);
  }, [effectiveEndMs]);

  // Content mode: PDF sharing (wake overlay still renders on top)
  if (docState?.display_mode === 'content' && meetingId) {
    return (
      <div className="rs-stage">
        <ContentModeDisplay
          pageNumber={docState.page_number}
          meetingId={meetingId}
        />
        {glowProgress > 0 && overrunMs === 0 && <TimeGlowOverlay progress={glowProgress} />}
        {overrunMs > 0 && <MeetingOverrunOverlay overrunMs={overrunMs} pulseIntensity={pulseIntensity} />}
        {isThinkingActive && <WakeWordOverlay />}
      </div>
    );
  }

  // View state
  const isPreMeeting = status === 'created' || status === 'starting';
  const allItemsDone =
    agendaItems.length > 0 &&
    agendaItems.every((i) => i.status === 'completed' || i.status === 'skipped');
  const isRecap =
    status === 'ended' || status === 'ending' || allItemsDone;
  const isLive = !isPreMeeting && !isRecap;

  return (
    <div className="rs-stage">
      <RithmoGlow />
      <Header title={meetingTitle} />
      <div className="rs-body">
        {isPreMeeting && <PreMeetingDisplay items={agendaItems} />}
        {isLive && (
          <LiveItemDisplay
            item={currentItem}
            currentIdx={currentIdx}
            totalItems={agendaItems.length}
            speakerName={speakerName}
          />
        )}
        {isRecap && (
          <RecapDisplay
            items={agendaItems}
            captureLog={captureLog}
            elapsedMs={localElapsedMs}
            interimAnalysis={interimAnalysis}
            liveScorecard={liveScorecard}
          />
        )}
      </div>
      <BannerZone banner={activeBanner} />
      {!isRecap && <CaptureStrip entries={captureLog} />}
      <Footer elapsedMs={localElapsedMs} />
      {glowProgress > 0 && overrunMs === 0 && <TimeGlowOverlay progress={glowProgress} />}
      {overrunMs > 0 && <MeetingOverrunOverlay overrunMs={overrunMs} pulseIntensity={pulseIntensity} />}
      {isThinkingActive && <WakeWordOverlay />}
    </div>
  );
}
